'use client'

/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Theme provider component for Material-UI theming with dark/light mode support.
 * Assembles palette, typography, glass materials, and component overrides into
 * a unified MUI theme.
 */

import React, { useMemo } from 'react'
import { createTheme, ThemeProvider, useMediaQuery } from '@mui/material'

// Side-effect import — registers MUI type augmentations
import './augmentations'

import { UIPalette, type ThemeProps } from './palette'
import { typographyConfig } from './typography'
import { avatarOverrides } from './overrides/avatars'
import { badgeOverrides } from './overrides/badges'
import { buttonOverrides } from './overrides/buttons'
import { inputOverrides } from './overrides/inputs'
import { chipOverrides } from './overrides/chips'
import { listOverrides } from './overrides/lists'
import { surfaceOverrides } from './overrides/surfaces'
import { navigationOverrides } from './overrides/navigation'
import { tableOverrides } from './overrides/tables'
import { breakpoints as breakpointTokens } from './tokens'

// Re-exports (public API)
export { colorConverter } from './colorConverter'
export { glassMaterial, glassMaterialCard, glassMaterialLevel } from './glass'
export { tokens, radii, spacing, sizing, inputs, transitions } from './tokens'
export type { TDesignTokens } from './tokens'
export type { TPaletteColor, TPaletteDefinition, ThemeProps } from './palette'

export function Theme({
  children,
  url,
  mode: forcedMode,
  density = 'regular',
  reducedMotion: forcedReducedMotion,
}: ThemeProps) {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const mode: 'light' | 'dark' =
    forcedMode ?? (prefersDarkMode ? 'dark' : 'light')
  const reducedMotion = forcedReducedMotion ?? prefersReducedMotion

  const theme = useMemo(() => {
    // Per-instance white-labelling: the URL/Entity accent overrides resolve
    // here and must reach every component (`instanceTheming.test.tsx`).
    const palette = UIPalette(mode, url)

    return createTheme({
      // Plan task 1.12: the approved scale (xs 0 / sm 600 / md 900 / lg 1200
      // / xl 1536), replacing the legacy 480/768/992/1280 — task 1.1 exported
      // it as `tokens.breakpoints` but left `createTheme` on the old values
      // until the switch itself could be reviewed (it reflows every portal
      // and, since hyperseguros-web shares this `Theme`, the website too —
      // see that app's `LegacyBreakpointsProvider` for why the website's
      // layout does not move with this switch).
      breakpoints: {
        values: breakpointTokens,
      },
      typography: typographyConfig(palette, density),
      palette: {
        mode,
        white: { ...palette.white },
        black: { ...palette.black },
        background: {
          default: palette.background.main,
          paper: palette.paper.main,
        },
        colorModeWhite: {
          main: '#FFFFFF',
          light: '#FFFFFF',
          dark: 'rgb(44,44,46)',
        },
        primary: {
          ...(palette.primary.avatarColor && {
            avatarColor: palette.primary.avatarColor,
          }),
          main: palette.primary.main,
          light: palette.primary.light,
          dark: palette.primary.dark,
          // Only when the URL forces a colour ON the primary; otherwise MUI
          // computes contrastText exactly as before.
          ...(palette.primary.forcedContrastText && {
            contrastText: palette.primary.forcedContrastText,
            forcedContrastText: palette.primary.forcedContrastText,
          }),
        },
        secondary: {
          main: palette.secondary.main,
          light: palette.secondary.light,
          dark: palette.secondary.dark,
        },
        text: {
          primary: palette.text.main,
          secondary: palette.textSecondary.main,
          disabled: palette.textDisabled.main,
        },
        error: { ...palette.error },
        warning: { ...palette.warning },
        success: { ...palette.success },
        info: { ...palette.info },
        gray: { ...palette.gray },
        gray2: { ...palette.gray2 },
        gray3: { ...palette.gray3 },
        gray4: { ...palette.gray4 },
        gray5: { ...palette.gray5 },
        gray6: { ...palette.gray6 },
        teal: { ...palette.teal },
        cyan: { ...palette.cyan },
        blue: { ...palette.blue },
      },
      components: {
        ...surfaceOverrides(mode, density),
        ...avatarOverrides(),
        ...badgeOverrides(palette),
        ...chipOverrides(palette, mode),
        ...buttonOverrides(palette, mode, reducedMotion),
        ...inputOverrides(palette, mode, density, reducedMotion),
        ...listOverrides(palette, mode, density, reducedMotion),
        ...navigationOverrides(palette, mode, density, reducedMotion),
        ...tableOverrides(palette, mode, density, reducedMotion),
      },
    })
  }, [mode, url, density, reducedMotion])

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
